import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { HttpClient } from '@angular/common/http';
import { EmailDataService } from '../../email/email.data.service';
import { ComposeEmailComponent } from '../../email/compose-email/compose-email.component';
import { UserService } from '../../../services/core/user.service';
import { environment } from '../../../../environments/environment';

@Component({
    selector: 'app-module-emails',
    templateUrl: './module-emails.component.html',
    styleUrls: ['./module-emails.component.scss'],
    standalone: false
})
export class ModuleEmailsComponent implements OnInit, OnDestroy {
  @Input() module_id: any;
  @Input() record_id: any;
  @Input() isLocked: boolean = false;

  // Compose state
  hasEmailAccounts = false;

  // Folder state
  activeFolder: 'inbox' | 'outbox' | 'internal_draft' = 'inbox';
  inboxCount = 0;
  sentCount = 0;
  draftCount = 0;

  // Email list state
  emails: any[] = [];
  selectedEmail: any = null;
  threadEmails: any[] = [];
  expandedMessageIds: Set<number> = new Set();
  isLoading = false;
  isDetailLoading = false;
  searchText = '';
  total = 0;

  private searchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();

  constructor(
    private emailDataService: EmailDataService,
    private dialog: MatDialog,
    private http: HttpClient,
    private userService: UserService
  ) {}

  ngOnInit(): void {
    this.searchSubject.pipe(
      debounceTime(300),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.loadEmails();
    });

    this.checkUserEmailAccounts();
    this.loadFolderCounts();
    this.loadEmails();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Check if auth user has email accounts
   */
  checkUserEmailAccounts(): void {
    const userId = this.userService.getID();
    const apiUrl = `${environment.apiBaseUrl}${environment.apiUrl}`;

    this.http.get(`${apiUrl}/emails/user-accounts/${userId}`)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          if (response?.success && response?.data?.accounts?.length > 0) {
            this.hasEmailAccounts = true;
          }
        }
      });
  }

  /**
   * Open compose email dialog
   */
  openComposeDialog(): void {
    const dialogRef = this.dialog.open(ComposeEmailComponent, {
      width: '600px',
      height: '100vh',
      position: { right: '0' },
      panelClass: ['compose-email-dialog', 'righthand-drawer'],
      hasBackdrop: true,
      data: {
        mode: 'compose',
        email: null,
        sessionId: null,
        inboxId: null,
        quotedContent: null,
        moduleId: this.module_id,
        moduleDataId: this.record_id,
        // Save draft here saves an INTERNAL, record-scoped draft (not a provider draft).
        internalDraft: true,
      }
    });

    dialogRef.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(result => {
        if (result?.sent || result?.internalDrafted) {
          this.loadFolderCounts();
          this.loadEmails();
        }
      });
  }

  /**
   * Reopen an internal draft in the compose dialog for editing/sending. Its saved
   * attachments are refetched from S3 as File objects and pre-attached; the draft
   * id is passed so a re-save updates it and a send removes it.
   */
  async openInternalDraft(draft: any): Promise<void> {
    const files: File[] = [];
    for (const att of (draft.attachments || [])) {
      if (!att?.url) { continue; }
      try {
        const resp = await fetch(att.url, { mode: 'cors', cache: 'reload' });
        if (!resp.ok) { continue; }
        const blob = await resp.blob();
        files.push(new File([blob], att.original_name || att.name || 'attachment', { type: blob.type || 'application/octet-stream' }));
      } catch { /* skip unreachable attachment */ }
    }

    const toContacts = (draft.to_emails || []).map((e: string) => ({ email: e, name: e }));
    const ccContacts = (draft.cc_emails || []).map((e: string) => ({ email: e, name: e }));
    const bccContacts = (draft.bcc_emails || []).map((e: string) => ({ email: e, name: e }));

    const dialogRef = this.dialog.open(ComposeEmailComponent, {
      width: '600px',
      height: '100vh',
      position: { right: '0' },
      panelClass: ['compose-email-dialog', 'righthand-drawer'],
      hasBackdrop: true,
      data: {
        mode: 'compose',
        moduleId: this.module_id,
        moduleDataId: this.record_id,
        internalDraft: true,
        internalDraftId: draft.id,
        prefillRecipients: toContacts,
        prefillCcRecipients: ccContacts,
        prefillBccRecipients: bccContacts,
        prefillSubject: draft.subject || '',
        prefillBody: draft.body_html || '',
        preAttachedFiles: files,
      }
    });

    dialogRef.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(result => {
        if (result?.sent || result?.internalDrafted) {
          this.loadFolderCounts();
          this.loadEmails();
        }
      });
  }

  /**
   * Load email counts for both folders
   */
  loadFolderCounts(): void {
    if (!this.module_id || !this.record_id) return;

    // Fetch inbox count
    this.emailDataService.fetchModuleEmails(this.module_id, this.record_id, { type: 'inbox', per_page: 1 })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          if (response?.success && response?.data) {
            this.inboxCount = response.data.total || 0;
          }
        }
      });

    // Fetch outbox count
    this.emailDataService.fetchModuleEmails(this.module_id, this.record_id, { type: 'outbox', per_page: 1 })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          if (response?.success && response?.data) {
            this.sentCount = response.data.total || 0;
          }
        }
      });

    // Fetch internal-draft count
    this.emailDataService.fetchModuleEmails(this.module_id, this.record_id, { type: 'internal_draft', per_page: 1 })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          if (response?.success && response?.data) {
            this.draftCount = response.data.total || 0;
          }
        }
      });
  }

  /**
   * Switch active folder
   */
  selectFolder(folder: 'inbox' | 'outbox' | 'internal_draft'): void {
    if (this.activeFolder === folder) return;
    this.activeFolder = folder;
    this.selectedEmail = null;
    this.threadEmails = [];
    this.loadEmails();
  }

  /**
   * Load emails for the active folder
   */
  loadEmails(): void {
    if (!this.module_id || !this.record_id) return;

    this.isLoading = true;
    const filters: any = { type: this.activeFolder };

    if (this.searchText.trim()) {
      filters.search = this.searchText.trim();
    }

    this.emailDataService.fetchModuleEmails(this.module_id, this.record_id, filters)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          if (response?.success && response?.data) {
            this.emails = response.data.emails || [];
            this.total = response.data.total || 0;
          } else {
            this.emails = [];
            this.total = 0;
          }
          this.isLoading = false;

          // Auto-select first email if available and none selected.
          // Skip for internal drafts — selecting a draft opens the compose dialog,
          // and we must not auto-open it on load.
          if (this.activeFolder !== 'internal_draft' && this.emails.length > 0 && !this.selectedEmail) {
            this.selectEmail(this.emails[0]);
          }
        },
        error: () => {
          this.emails = [];
          this.total = 0;
          this.isLoading = false;
        }
      });
  }

  onSearchChange(): void {
    this.searchSubject.next(this.searchText);
  }

  /**
   * Select an email and load thread if applicable
   */
  selectEmail(email: any): void {
    // Internal drafts reopen in the compose dialog for editing/sending — not the
    // inline reader.
    if (email?.type === 'internal_draft') {
      this.openInternalDraft(email);
      return;
    }

    this.selectedEmail = email;
    this.threadEmails = [];
    this.expandedMessageIds.clear();

    // If thread has multiple messages, load full conversation
    if (email.thread_id && email.thread_message_count > 1) {
      this.isDetailLoading = true;
      this.emailDataService.fetchThreadFromAPI(email.thread_id)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response: any) => {
            if (response?.success && response?.data?.emails) {
              this.threadEmails = response.data.emails;
              // Expand the last (latest) message by default
              if (this.threadEmails.length > 0) {
                const lastMsg = this.threadEmails[this.threadEmails.length - 1];
                this.expandedMessageIds.add(lastMsg.id);
              }
            }
            this.isDetailLoading = false;
          },
          error: () => {
            this.isDetailLoading = false;
          }
        });
    }
  }

  isMessageExpanded(messageId: number): boolean {
    return this.expandedMessageIds.has(messageId);
  }

  toggleMessageExpand(messageId: number): void {
    if (this.expandedMessageIds.has(messageId)) {
      this.expandedMessageIds.delete(messageId);
    } else {
      this.expandedMessageIds.add(messageId);
    }
  }

  // ==================== Reply / Reply All / Forward ====================
  // All actions use mode: 'compose' so they are treated as new emails
  // using POST /api/emails/send (no threading)

  /**
   * Reply to an email - opens compose with To pre-filled with sender
   */
  replyEmail(email: any): void {
    const toRecipients = [{ name: email.from_name || email.from_email, email: email.from_email, type: 'external' }];
    const subject = 'Re: ' + (email.subject || '').replace(/^Re:\s*/i, '');
    const body = this.generateQuotedContent(email);

    this.openComposeWithPrefill(toRecipients, [], subject, body);
  }

  /**
   * Reply all to an email - opens compose with To + CC pre-filled
   */
  replyAllEmail(email: any): void {
    const toRecipients: any[] = [];
    if (email.from_email) {
      toRecipients.push({ name: email.from_name || email.from_email, email: email.from_email, type: 'external' });
    }
    if (Array.isArray(email.to_emails)) {
      email.to_emails.forEach((addr: string) => {
        if (!toRecipients.some((r: any) => r.email === addr)) {
          toRecipients.push({ name: addr, email: addr, type: 'external' });
        }
      });
    }

    const ccRecipients: any[] = [];
    if (Array.isArray(email.cc_emails)) {
      email.cc_emails.forEach((addr: string) => {
        ccRecipients.push({ name: addr, email: addr, type: 'external' });
      });
    }

    const subject = 'Re: ' + (email.subject || '').replace(/^Re:\s*/i, '');
    const body = this.generateQuotedContent(email);

    this.openComposeWithPrefill(toRecipients, ccRecipients, subject, body);
  }

  /**
   * Forward an email - opens compose with body and attachments pre-filled, To empty
   */
  forwardEmail(email: any): void {
    const subject = 'Fwd: ' + (email.subject || '');
    const body = this.generateForwardContent(email);
    const forwardAttachments = this.getAttachmentsForForward(email);

    this.openComposeWithPrefill([], [], subject, body, forwardAttachments);
  }

  /**
   * Open compose dialog with pre-filled data (all treated as new email)
   */
  private openComposeWithPrefill(
    toRecipients: any[],
    ccRecipients: any[],
    subject: string,
    body: string,
    forwardAttachments: any[] = []
  ): void {
    const dialogRef = this.dialog.open(ComposeEmailComponent, {
      width: '600px',
      height: '100vh',
      position: { right: '0' },
      panelClass: ['compose-email-dialog', 'righthand-drawer'],
      hasBackdrop: true,
      data: {
        mode: 'compose',
        prefillRecipients: toRecipients,
        prefillCcRecipients: ccRecipients,
        prefillSubject: subject,
        forwardContent: body,
        forwardAttachments: forwardAttachments.length > 0 ? forwardAttachments : undefined,
        moduleId: this.module_id,
        moduleDataId: this.record_id
      }
    });

    dialogRef.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(result => {
        if (result?.sent) {
          this.loadFolderCounts();
          this.loadEmails();
        }
      });
  }

  private generateQuotedContent(email: any): string {
    const date = (email.received_at || email.sent_at) ? new Date(email.received_at || email.sent_at).toLocaleString() : '';
    const fromName = email.from_name || 'Unknown';
    const fromEmail = email.from_email || '';
    const toRecipients = Array.isArray(email.to_emails) && email.to_emails.length > 0
      ? email.to_emails.join(', ')
      : 'Recipients';
    const body = email.body_html || email.body_text || '';

    return `
      <br><br>
      <div class="gmail_quote">
        <div>---------- Original message ---------</div>
        <div><strong>From:</strong> ${fromName}${fromEmail ? ' &lt;' + fromEmail + '&gt;' : ''}</div>
        <div><strong>Date:</strong> ${date}</div>
        <div><strong>Subject:</strong> ${email.subject || ''}</div>
        <div><strong>To:</strong> ${toRecipients}</div>
        <br>
        <div>${body}</div>
      </div>
    `;
  }

  private generateForwardContent(email: any): string {
    const date = (email.received_at || email.sent_at) ? new Date(email.received_at || email.sent_at).toLocaleString() : '';
    const fromName = email.from_name || 'Unknown';
    const fromEmail = email.from_email || '';
    const toRecipients = Array.isArray(email.to_emails) && email.to_emails.length > 0
      ? email.to_emails.join(', ')
      : 'Recipients';
    const body = email.body_html || email.body_text || '';

    return `
      <br><br>
      <div class="gmail_quote">
        <div>---------- Forwarded message ---------</div>
        <div><strong>From:</strong> ${fromName}${fromEmail ? ' &lt;' + fromEmail + '&gt;' : ''}</div>
        <div><strong>Date:</strong> ${date}</div>
        <div><strong>Subject:</strong> ${email.subject || ''}</div>
        <div><strong>To:</strong> ${toRecipients}</div>
        <br>
        <div>${body}</div>
      </div>
    `;
  }

  private getAttachmentsForForward(email: any): any[] {
    if (Array.isArray(email.attachments) && email.attachments.length > 0) {
      return email.attachments.map((att: any) => ({
        id: att.id || 0,
        name: att.name || att.file_name || 'attachment',
        size: att.size || att.file_size || 0,
        type: att.type || att.mime_type || 'application/octet-stream',
        url: att.url || att.s3_url || ''
      }));
    }
    return [];
  }

  // ==================== Display Helpers ====================

  getSenderDisplay(email: any): string {
    if (email.type === 'inbox') {
      return email.from_name || email.from_email || 'Unknown';
    }
    const toEmails = email.to_emails;
    if (Array.isArray(toEmails) && toEmails.length > 0) {
      return 'To: ' + toEmails[0];
    }
    return 'To: Unknown';
  }

  getPreviewText(email: any): string {
    const text = email.snippet || email.body_text || '';
    return text.length > 120 ? text.substring(0, 120) + '...' : text;
  }

  hasAttachments(email: any): boolean {
    return Array.isArray(email.attachments) && email.attachments.length > 0;
  }

  getAttachmentCount(email: any): number {
    return Array.isArray(email.attachments) ? email.attachments.length : 0;
  }

  formatDate(dateStr: string): string {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();

    if (isToday) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }

    const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays < 7) {
      return date.toLocaleDateString([], { weekday: 'short' });
    }

    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  }

  formatFullDate(dateStr: string): string {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString([], {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  }

  getEmailDate(email: any): string {
    if (email.type === 'internal_draft') {
      return email.updated_at || email.created_at;
    }
    return email.type === 'inbox' ? email.received_at : (email.sent_at || email.received_at);
  }

  getAvatarInitials(email: any): string {
    const name = email.from_name || email.from_email || '';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  }

  getAvatarColor(email: any): string {
    const name = email.from_name || email.from_email || 'U';
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    const colors = ['#f97316', '#0078d4', '#107c10', '#8764b8', '#e74856', '#00b7c3', '#ca5010'];
    return colors[Math.abs(hash) % colors.length];
  }

  getRecipientsText(email: any): string {
    const parts: string[] = [];
    if (email.to_emails?.length) {
      parts.push('To: ' + email.to_emails.join(', '));
    }
    if (email.cc_emails?.length) {
      parts.push('CC: ' + email.cc_emails.join(', '));
    }
    return parts.join(' | ');
  }
}
